import {
  APIComponentAttributes,
  APIEntityType,
  APIFieldAttributes,
  APIReferenceAttributes,
} from '@ardoq/api-types';
import { logError } from '@ardoq/logging';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { getModelIdFromReference } from './getModelIdFromReference';
import { getEntityById } from './getEntityById';

type EntityModelAndTypeIDsReducerAccumulator = {
  readonly model: string | null;
  readonly typeIDs: string[];
};

export const getEntityModelAndTypeIDs = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityIDs: string[],
  enhancedScopeData: EnhancedScopeData
) =>
  entityIDs.reduce<EntityModelAndTypeIDsReducerAccumulator>(
    (acc, entityID) => {
      const { model, typeId } = getEntityModelAndTypeID(
        entityType,
        entityID,
        enhancedScopeData
      );
      if (!model || !typeId || acc.typeIDs.includes(typeId)) {
        return acc;
      }
      return { model, typeIDs: [...acc.typeIDs, typeId] };
    },
    { model: null, typeIDs: [] }
  );

export const getEntityModelAndTypeID = (
  entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
  entityId: string,
  enhancedScopeData: EnhancedScopeData
) => {
  const entity = getEntityById(entityType, entityId, enhancedScopeData);
  if (!entity) {
    logError(
      new Error(`Unknown entity of type in getEntityModelAndTypeID()`),
      null,
      { entityType }
    );
    return { model: null, typeId: null };
  }
  return {
    model: getEntityModelId(entityId, entityType, enhancedScopeData),
    typeId: getEntityTypeId(entityId, entityType, enhancedScopeData),
  };
};
const getEntityModelId = (
  entityId: string,
  entityType: APIEntityType,
  enhancedScopeData: EnhancedScopeData
) => {
  if (
    ![
      APIEntityType.COMPONENT,
      APIEntityType.REFERENCE,
      APIEntityType.FIELD,
    ].includes(entityType)
  ) {
    logError(
      new Error(`Cannot find modelId for entity of type in getEntityModelId()`),
      null,
      { entityType }
    );

    return null;
  }
  const entity = getEntityById(entityType, entityId, enhancedScopeData);
  if (entityType === APIEntityType.REFERENCE) {
    return getModelIdFromReference(
      entity as APIReferenceAttributes,
      enhancedScopeData
    );
  } else if (entityType === APIEntityType.COMPONENT) {
    return (entity as APIComponentAttributes).model;
  } else if (entityType === APIEntityType.FIELD) {
    return (entity as APIFieldAttributes).model;
  }
};
const getEntityTypeId = (
  entityId: string,
  entityType: APIEntityType,
  enhancedScopeData: EnhancedScopeData
) => {
  const entity = getEntityById(entityType, entityId, enhancedScopeData);
  if (entityType === APIEntityType.COMPONENT) {
    return (entity as APIComponentAttributes).typeId;
  } else if (entityType === APIEntityType.REFERENCE) {
    return String((entity as APIReferenceAttributes).type);
  }
  return null;
};
